/**
 * Alerts Panel Component
 *
 * Displays KPI breach alerts with two-step acknowledgment workflow:
 * Step 1: Single-tap ACK (marks as seen, timestamps, records user)
 * Step 2: Action decision (Yes = create task, No = select reason)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  alertAPI,
  taskAPI,
  AlertDetail,
  AlertStatus,
  AlertSeverity,
  AlertType,
  AlertFilters,
  UnacknowledgedAlertCount,
  TaskCategory,
  NoActionReason,
} from '../services/api';
import CreateTaskModal from './CreateTaskModal';
import AlertDetailModal from './AlertDetailModal';

const SEVERITY_STYLES: Record<AlertSeverity, { bg: string; text: string; border: string; icon: string }> = {
  critical: {
    bg: 'bg-red-50 dark:bg-red-900/20',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-400 dark:border-red-600',
    icon: '🔴',
  },
  warning: {
    bg: 'bg-yellow-50 dark:bg-yellow-900/20',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-400 dark:border-yellow-600',
    icon: '🟡',
  },
  info: {
    bg: 'bg-blue-50 dark:bg-blue-900/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-400 dark:border-blue-600',
    icon: '🔵',
  },
};

const STATUS_LABELS: Record<AlertStatus, string> = {
  unacknowledged: 'Unacknowledged',
  acknowledged: 'Acknowledged',
  resolved: 'Resolved',
};

const ALERT_TYPE_LABELS: Record<AlertType, string> = {
  high_scrap: 'High Scrap',
  high_downtime: 'High Downtime',
  low_efficiency: 'Low Efficiency',
  no_data: 'No Data',
};

const NO_ACTION_REASON_LABELS: Record<NoActionReason, string> = {
  resolved_on_shift: 'Resolved on shift',
  monitoring: 'Monitoring / expected recovery',
  needs_review: 'Unsure - needs review',
  other: 'Other',
};

interface AlertsPanelProps {
  collapsed?: boolean;
  onToggleCollapse?: () => void;
  dateFrom?: string;
  dateTo?: string;
  departmentId?: number | '';
  shiftName?: string;
}

const AlertsPanel: React.FC<AlertsPanelProps> = ({ collapsed = false, onToggleCollapse, dateFrom, dateTo, departmentId, shiftName }) => {
  const [alerts, setAlerts] = useState<AlertDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [unacknowledgedCount, setUnacknowledgedCount] = useState<UnacknowledgedAlertCount | null>(null);
  const [categories, setCategories] = useState<TaskCategory[]>([]);

  // Filters
  const [filterStatus, setFilterStatus] = useState<AlertStatus | ''>('unacknowledged');
  const [filterSeverity, setFilterSeverity] = useState<AlertSeverity | ''>('');
  const [filterType, setFilterType] = useState<AlertType | ''>('');
  // Date range is driven by the dashboard filters (passed via props)
  const filterDateFrom = dateFrom || '';
  const filterDateTo = dateTo || '';

  // Modal states
  const [selectedAlert, setSelectedAlert] = useState<AlertDetail | null>(null);
  const [showActionDecisionModal, setShowActionDecisionModal] = useState(false);
  const [showNoActionForm, setShowNoActionForm] = useState(false);
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false);
  const [showAlertDetailModal, setShowAlertDetailModal] = useState(false);
  const [alertDetailId, setAlertDetailId] = useState<number | null>(null);

  // Form state for no-action flow
  const [noActionReason, setNoActionReason] = useState<NoActionReason | ''>('');
  const [resolutionNotes, setResolutionNotes] = useState('');

  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Build base filters from dashboard props (department, shift, date)
  const buildBaseFilters = useCallback((): AlertFilters => {
    const filters: AlertFilters = {};
    if (filterDateFrom) filters.date_from = filterDateFrom;
    if (filterDateTo) filters.date_to = filterDateTo;
    if (departmentId) filters.department_id = Number(departmentId);
    if (shiftName) filters.shift_name = shiftName;
    return filters;
  }, [filterDateFrom, filterDateTo, departmentId, shiftName]);

  const fetchUnacknowledgedCount = useCallback(async () => {
    try {
      // Fetch unacknowledged count with dashboard filters applied
      const filters: AlertFilters = {
        ...buildBaseFilters(),
        status: 'unacknowledged' as AlertStatus,
        limit: 1, // We only need the total count
      };
      const response = await alertAPI.list(filters);
      // Derive counts from the filtered total
      // For severity breakdown, count from a small fetch
      const detailFilters: AlertFilters = {
        ...buildBaseFilters(),
        status: 'unacknowledged' as AlertStatus,
        limit: 200,
      };
      const detailResponse = await alertAPI.list(detailFilters);
      const critical = detailResponse.items.filter(a => a.severity === 'critical').length;
      const warning = detailResponse.items.filter(a => a.severity === 'warning').length;
      setUnacknowledgedCount({
        total: detailResponse.total,
        critical,
        warning,
      });
    } catch (err) {
      console.error('Failed to fetch alert count:', err);
    }
  }, [buildBaseFilters]);

  const fetchAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const filters: AlertFilters = { ...buildBaseFilters(), limit: 20 };
      if (filterStatus) filters.status = filterStatus;
      if (filterSeverity) filters.severity = filterSeverity;
      if (filterType) filters.alert_type = filterType;

      const response = await alertAPI.list(filters);
      setAlerts(response.items);
    } catch (err) {
      console.error('Failed to fetch alerts:', err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterSeverity, filterType, buildBaseFilters]);

  const fetchCategories = async () => {
    try {
      const cats = await taskAPI.getCategories();
      setCategories(cats);
    } catch (err) {
      console.error('Failed to fetch categories:', err);
    }
  };

  useEffect(() => {
    fetchUnacknowledgedCount();
    fetchAlerts();
    fetchCategories();
  }, [fetchAlerts, fetchUnacknowledgedCount]);

  /**
   * Step 1: Quick acknowledge (single tap)
   * Immediately marks as acknowledged, then shows action decision modal
   */
  const handleQuickAcknowledge = async (alert: AlertDetail) => {
    try {
      setProcessing(true);
      setError(null);

      // Immediately acknowledge
      const updatedAlert = await alertAPI.acknowledge(alert.id);

      // Update local state
      setAlerts(prev => prev.map(a => a.id === alert.id ? updatedAlert : a));
      fetchUnacknowledgedCount();

      // Show action decision modal
      setSelectedAlert(updatedAlert);
      setShowActionDecisionModal(true);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to acknowledge alert');
    } finally {
      setProcessing(false);
    }
  };

  /**
   * Handle action decision: Yes = needs task
   */
  const handleActionNeededYes = () => {
    setShowActionDecisionModal(false);
    setShowCreateTaskModal(true);
  };

  /**
   * Handle action decision: No = show reason form
   */
  const handleActionNeededNo = () => {
    setShowActionDecisionModal(false);
    setShowNoActionForm(true);
  };

  /**
   * Submit no-action decision with required reason
   */
  const handleSubmitNoAction = async () => {
    if (!selectedAlert || !noActionReason) {
      setError('Please select a reason');
      return;
    }

    try {
      setProcessing(true);
      setError(null);

      await alertAPI.setActionDecision(selectedAlert.id, {
        action_needed: false,
        no_action_reason: noActionReason,
        resolution_notes: resolutionNotes || undefined,
      });

      // Reset and refresh
      resetModals();
      fetchAlerts();
      fetchUnacknowledgedCount();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to submit decision');
    } finally {
      setProcessing(false);
    }
  };

  const handleTaskCreated = async () => {
    if (selectedAlert) {
      // Mark that action was needed and task was created
      try {
        await alertAPI.setActionDecision(selectedAlert.id, {
          action_needed: true,
        });
      } catch (err) {
        console.error('Failed to record action decision:', err);
      }
    }
    resetModals();
    fetchAlerts();
  };

  const resetModals = () => {
    setShowActionDecisionModal(false);
    setShowNoActionForm(false);
    setShowCreateTaskModal(false);
    setSelectedAlert(null);
    setNoActionReason('');
    setResolutionNotes('');
    setError(null);
  };

  const handleResolve = async (alertId: number) => {
    try {
      await alertAPI.resolve(alertId);
      fetchAlerts();
      fetchUnacknowledgedCount();
    } catch (err: any) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const formatDateTime = (dateStr: string): string => {
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  /**
   * Format KPI values appropriately:
   * - Minutes (Downtime): no decimals
   * - Percentages (Scrap Rate, Efficiency): 2 decimal places
   */
  const formatKpiValue = (value: number | string | null, kpiName: string): string => {
    if (value === null || value === undefined) return '';
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    if (isNaN(numValue)) return String(value);

    // Downtime is in minutes - no decimals
    if (kpiName.toLowerCase().includes('downtime')) {
      return Math.round(numValue).toString();
    }
    // Percentages and other values - 2 decimal places
    return numValue.toFixed(2);
  };

  // Determine panel color based on alerts
  const getPanelStyle = () => {
    if (unacknowledgedCount && unacknowledgedCount.critical > 0) {
      return SEVERITY_STYLES.critical;
    }
    if (unacknowledgedCount && unacknowledgedCount.warning > 0) {
      return SEVERITY_STYLES.warning;
    }
    return {
      bg: 'bg-green-50 dark:bg-green-900/20',
      text: 'text-green-700 dark:text-green-300',
      border: 'border-green-400 dark:border-green-600',
      icon: '🟢',
    };
  };

  const panelStyle = getPanelStyle();

  /**
   * Get status badge with additional context for acknowledged alerts
   */
  const getStatusBadge = (alert: AlertDetail) => {
    if (alert.status === 'acknowledged' && alert.action_needed === null) {
      return (
        <span className="text-xs px-2 py-0.5 rounded-full bg-orange-200 dark:bg-orange-800 text-orange-800 dark:text-orange-200">
          Pending Action
        </span>
      );
    }
    return (
      <span className="text-xs px-2 py-0.5 rounded-full bg-gray-200 dark:bg-gray-700" style={{ color: 'var(--text-primary)' }}>
        {STATUS_LABELS[alert.status]}
      </span>
    );
  };

  return (
    <div className={`rounded-xl shadow-lg border-2 overflow-hidden ${panelStyle.bg} ${panelStyle.border}`}>
      {/* Header */}
      <div
        className="p-4 cursor-pointer flex items-center justify-between"
        onClick={onToggleCollapse}
      >
        <div className="flex items-center gap-3">
          <span className="text-xl">{panelStyle.icon}</span>
          <span className={`font-bold text-lg ${panelStyle.text}`}>
            KPI Alerts
            {unacknowledgedCount && unacknowledgedCount.total > 0 && (
              <span className="ml-2">({unacknowledgedCount.total} unacknowledged)</span>
            )}
          </span>
        </div>
        <span className={`text-xl transition-transform ${collapsed ? '' : 'rotate-180'}`}>
          ▼
        </span>
      </div>

      {/* Content */}
      {!collapsed && (
        <div className="p-4 pt-0 space-y-4">
          {/* Filters */}
          <div className="flex flex-wrap gap-4 p-3 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Status:
              </label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value as AlertStatus | '')}
                className="border rounded px-2 py-1 text-sm"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              >
                <option value="">All</option>
                <option value="unacknowledged">Unacknowledged</option>
                <option value="acknowledged">Acknowledged</option>
                <option value="resolved">Resolved</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Severity:
              </label>
              <select
                value={filterSeverity}
                onChange={(e) => setFilterSeverity(e.target.value as AlertSeverity | '')}
                className="border rounded px-2 py-1 text-sm"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              >
                <option value="">All</option>
                <option value="critical">Critical</option>
                <option value="warning">Warning</option>
                <option value="info">Info</option>
              </select>
            </div>
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>
                Type:
              </label>
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value as AlertType | '')}
                className="border rounded px-2 py-1 text-sm"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              >
                <option value="">All</option>
                <option value="high_scrap">High Scrap</option>
                <option value="high_downtime">High Downtime</option>
                <option value="low_efficiency">Low Efficiency</option>
                <option value="no_data">No Data</option>
              </select>
            </div>
            {(filterStatus || filterSeverity || filterType) && (
              <button
                onClick={() => {
                  setFilterStatus('unacknowledged');
                  setFilterSeverity('');
                  setFilterType('');
                }}
                className="text-xs px-2 py-1 rounded border hover:bg-gray-100 dark:hover:bg-gray-800"
                style={{ color: 'var(--text-secondary)', borderColor: 'var(--border-color)' }}
              >
                Reset
              </button>
            )}
          </div>

          {/* Alerts List */}
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : alerts.length === 0 ? (
            <div className="text-center py-6 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
              <span className="text-4xl mb-2 block">
                {filterStatus === 'unacknowledged' ? '✅' : '📋'}
              </span>
              <span style={{ color: 'var(--text-secondary)' }}>
                {filterStatus === 'unacknowledged'
                  ? 'No unacknowledged alerts - all clear!'
                  : 'No alerts match your filters'}
              </span>
            </div>
          ) : (
            <div className="space-y-2">
              {alerts.map((alert) => {
                const style = SEVERITY_STYLES[alert.severity];
                return (
                  <div
                    key={alert.id}
                    className={`p-3 rounded-lg border-2 ${style.bg} ${style.border}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span>{style.icon}</span>
                          <span className="text-xs font-mono px-1.5 py-0.5 rounded bg-gray-200 dark:bg-gray-700" style={{ color: 'var(--text-secondary)' }}>
                            #{alert.id}
                          </span>
                          <span className={`font-bold ${style.text}`}>
                            {ALERT_TYPE_LABELS[alert.alert_type]}
                          </span>
                          {getStatusBadge(alert)}
                          {alert.task_count > 0 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setAlertDetailId(alert.id);
                                setShowAlertDetailModal(true);
                              }}
                              className="text-xs px-2 py-0.5 rounded-full bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200 hover:bg-blue-300 dark:hover:bg-blue-700 cursor-pointer"
                            >
                              {alert.task_count} task{alert.task_count > 1 ? 's' : ''}
                            </button>
                          )}
                        </div>
                        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
                          <strong>{alert.kpi_name}</strong>
                          {alert.kpi_value !== null && (
                            <span>
                              : {formatKpiValue(alert.kpi_value, alert.kpi_name)}
                              {alert.threshold_value !== null && (
                                <span className="text-gray-500"> (threshold: {formatKpiValue(alert.threshold_value, alert.kpi_name)})</span>
                              )}
                            </span>
                          )}
                        </div>
                        {alert.description && (
                          <div className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                            {alert.description}
                          </div>
                        )}
                        <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                          {alert.department_name}
                          {alert.shift_name && ` • ${alert.shift_name}`}
                          {alert.machine_name && ` • ${alert.machine_name}`}
                          {' • '}
                          {formatDateTime(alert.created_at)}
                        </div>
                        {/* Show resolution info for resolved alerts */}
                        {alert.status === 'resolved' && alert.no_action_reason && (
                          <div className="text-sm mt-2 p-2 rounded bg-white/50 dark:bg-black/20">
                            <span className="font-medium">Resolution: </span>
                            {NO_ACTION_REASON_LABELS[alert.no_action_reason]}
                            {alert.resolution_notes && (
                              <span className="text-gray-600 dark:text-gray-400"> - {alert.resolution_notes}</span>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex flex-col gap-1">
                        {alert.status === 'unacknowledged' && (
                          <button
                            onClick={() => handleQuickAcknowledge(alert)}
                            disabled={processing}
                            className="px-3 py-1 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                          >
                            ACK
                          </button>
                        )}
                        {alert.status === 'acknowledged' && alert.action_needed === null && (
                          <>
                            <button
                              onClick={() => {
                                setSelectedAlert(alert);
                                setShowActionDecisionModal(true);
                              }}
                              className="px-3 py-1 text-xs font-medium rounded bg-orange-500 text-white hover:bg-orange-600"
                            >
                              Action?
                            </button>
                          </>
                        )}
                        {alert.status === 'acknowledged' && alert.action_needed !== null && (
                          <button
                            onClick={() => handleResolve(alert.id)}
                            className="px-3 py-1 text-xs font-medium rounded bg-green-600 text-white hover:bg-green-700"
                          >
                            Resolve
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Action Decision Modal (Step 2) */}
      {showActionDecisionModal && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-lg shadow-xl w-full max-w-sm p-6"
            style={{ background: 'var(--bg-secondary)' }}
          >
            <h3 className="text-lg font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
              Is action needed?
            </h3>

            <div className={`p-3 rounded-lg mb-6 ${SEVERITY_STYLES[selectedAlert.severity].bg} ${SEVERITY_STYLES[selectedAlert.severity].border} border`}>
              <div className={`font-medium text-sm ${SEVERITY_STYLES[selectedAlert.severity].text}`}>
                {ALERT_TYPE_LABELS[selectedAlert.alert_type]}: {selectedAlert.kpi_name}
              </div>
              <div className="text-xs mt-1" style={{ color: 'var(--text-secondary)' }}>
                {selectedAlert.machine_name || selectedAlert.department_name}
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleActionNeededYes}
                className="flex-1 py-3 text-lg font-bold rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <span className="text-xl">✓</span> Yes
              </button>
              <button
                onClick={handleActionNeededNo}
                className="flex-1 py-3 text-lg font-bold rounded-lg bg-gray-500 text-white hover:bg-gray-600 flex items-center justify-center gap-2"
              >
                <span className="text-xl">✗</span> No
              </button>
            </div>

            <button
              onClick={resetModals}
              className="w-full mt-4 py-2 text-sm font-medium rounded-md"
              style={{ color: 'var(--text-secondary)' }}
            >
              Decide later
            </button>
          </div>
        </div>
      )}

      {/* No Action Form Modal */}
      {showNoActionForm && selectedAlert && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div
            className="rounded-lg shadow-xl w-full max-w-md p-6"
            style={{ background: 'var(--bg-secondary)' }}
          >
            <h3 className="text-lg font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
              No action needed
            </h3>

            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-3 py-2 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Reason <span className="text-red-500">*</span>
              </label>
              <select
                value={noActionReason}
                onChange={(e) => setNoActionReason(e.target.value as NoActionReason)}
                className="w-full border rounded-md px-3 py-2"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              >
                <option value="">Select a reason...</option>
                <option value="resolved_on_shift">Resolved on shift</option>
                <option value="monitoring">Monitoring / expected recovery</option>
                <option value="needs_review">Unsure - needs review</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="mb-6">
              <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                Notes <span className="text-xs font-normal" style={{ color: 'var(--text-secondary)' }}>(optional)</span>
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={3}
                placeholder="Any additional context..."
                className="w-full px-3 py-2 border rounded-md"
                style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
              />
            </div>

            <div className="flex gap-2">
              <button
                onClick={() => {
                  setShowNoActionForm(false);
                  setShowActionDecisionModal(true);
                  setNoActionReason('');
                  setResolutionNotes('');
                }}
                className="flex-1 py-2 text-sm font-medium rounded-md"
                style={{ background: 'var(--bg-primary)', color: 'var(--text-primary)' }}
              >
                Back
              </button>
              <button
                onClick={handleSubmitNoAction}
                disabled={processing || !noActionReason}
                className="flex-1 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {processing ? 'Saving...' : 'Submit'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Task Modal */}
      {selectedAlert && (
        <CreateTaskModal
          isOpen={showCreateTaskModal}
          onClose={resetModals}
          categories={categories}
          onTaskCreated={handleTaskCreated}
          preselectedAlertId={selectedAlert.id}
        />
      )}

      {/* Alert Detail Modal */}
      <AlertDetailModal
        isOpen={showAlertDetailModal}
        onClose={() => {
          setShowAlertDetailModal(false);
          setAlertDetailId(null);
        }}
        alertId={alertDetailId}
      />
    </div>
  );
};

export default AlertsPanel;
